import { api } from "./client";
import type {
  AdminAuditRow,
  AdminClientRow,
  AdminDeviceRow,
  AdminEventRow,
  AdminLedgerRow,
  AdminMetrics,
  AdminOrgDetail,
  AdminOrgRow,
  AdminPaymentDetail,
  AdminPaymentRow,
  AdminProductRow,
  AdminPromoCode,
  AdminPromoRedemption,
  PlatformProductRow,
  ProductBranding,
  StaffMe,
  AdminSecurityEventRow,
  AdminUserDetail,
  AdminUserRow,
  StaffRow,
} from "./types";

export type Params = Record<string, string | number | undefined | null>;

export function withQuery(path: string, params: Params = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "" && value !== null)
      search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export const adminApi = {
  me: () => api<StaffMe>("/admin/me"),
  platformMetrics: (days?: number) =>
    api<{ days: number; items: PlatformProductRow[] }>(
      withQuery("/admin/metrics/platform", { days }),
    ),
  staff: () => api<StaffRow[]>("/admin/staff"),
  users: (params: Params) =>
    api<Page<AdminUserRow>>(withQuery("/admin/users", params)),
  user: (id: string) => api<AdminUserDetail>(`/admin/users/${id}`),
  suspend: (id: string, reason: string) =>
    api<{ status: string }>(`/admin/users/${id}/suspend`, {
      method: "POST",
      body: { reason },
    }),
  unsuspend: (id: string) =>
    api<{ status: string }>(`/admin/users/${id}/unsuspend`, { method: "POST" }),
  revokeAllSessions: (id: string) =>
    api<void>(`/admin/users/${id}/sessions/revoke-all`, { method: "POST" }),
  verifyEmail: (id: string) =>
    api<{ email_verified: boolean }>(`/admin/users/${id}/verify-email`, {
      method: "POST",
    }),
  resetLink: (id: string) =>
    api<{ link: string }>(`/admin/users/${id}/password-reset-link`, {
      method: "POST",
    }),
  notes: (id: string, body: { notes: string; flags: string[] }) =>
    api<{ notes: string | null; flags: string[] }>(`/admin/users/${id}/notes`, {
      method: "PUT",
      body,
    }),
  resetMfa: (id: string) =>
    api<{ mfa_enabled: boolean }>(`/admin/users/${id}/mfa/reset`, {
      method: "POST",
    }),
  deleteUser: (id: string) =>
    api<void>(`/admin/users/${id}/delete`, { method: "POST" }),
  orgs: (params: Params) =>
    api<Page<AdminOrgRow>>(withQuery("/admin/orgs", params)),
  org: (id: string) => api<AdminOrgDetail>(`/admin/orgs/${id}`),
  suspendBilling: (id: string, reason: string) =>
    api<{ billing_active: boolean }>(`/admin/orgs/${id}/billing/suspend`, {
      method: "POST",
      body: { reason },
    }),
  resumeBilling: (id: string, reason: string) =>
    api<{ billing_active: boolean }>(`/admin/orgs/${id}/billing/resume`, {
      method: "POST",
      body: { reason },
    }),
  adjustLedger: (
    id: string,
    body: { product: string; amount: number; note: string },
  ) =>
    api<{ entry_id: string; balance_after: number }>(
      `/admin/orgs/${id}/ledger/adjust`,
      { method: "POST", body },
    ),
  addOrgMember: (id: string, body: { email: string; role: string }) =>
    api<{ user_id: string }>(`/admin/orgs/${id}/members`, {
      method: "POST",
      body,
    }),
  removeOrgMember: (id: string, userId: string) =>
    api<void>(`/admin/orgs/${id}/members/${userId}`, { method: "DELETE" }),
  payments: (params: Params) =>
    api<Page<AdminPaymentRow>>(withQuery("/admin/payments", params)),
  payment: (id: string) => api<AdminPaymentDetail>(`/admin/payments/${id}`),
  refund: (id: string, body: { amount: number; reason: string }) =>
    api<{ refund_ref: string }>(`/admin/payments/${id}/refund`, {
      method: "POST",
      body,
    }),
  ledger: (params: Params) =>
    api<Page<AdminLedgerRow>>(withQuery("/admin/ledger", params)),
  // Every promo call names the product, so a role granted for that product counts.
  promoCodes: (product: string) =>
    api<Page<AdminPromoCode>>(
      withQuery("/admin/promo-codes", {
        product,
        exclude_invitations: "true",
        limit: 200,
      }),
    ),
  createPromoCode: (
    product: string,
    body: {
      code: string;
      credits: number;
      max_uses: number | null;
      expires_at: string | null;
    },
  ) =>
    api<AdminPromoCode>(withQuery("/admin/promo-codes", { product }), {
      method: "POST",
      body: { product, ...body },
    }),
  updatePromoCode: (
    product: string,
    id: string,
    body: { max_uses: number | null; expires_at: string | null },
  ) =>
    api<AdminPromoCode>(withQuery(`/admin/promo-codes/${id}`, { product }), {
      method: "PATCH",
      body,
    }),
  expirePromoCode: (product: string, id: string) =>
    api<void>(withQuery(`/admin/promo-codes/${id}`, { product }), {
      method: "DELETE",
    }),
  promoRedemptions: (product: string, id: string) =>
    api<Page<AdminPromoRedemption>>(
      withQuery(`/admin/promo-codes/${id}/redemptions`, {
        product,
        limit: 200,
      }),
    ),
  events: (params: Params) =>
    api<Page<AdminEventRow>>(withQuery("/admin/events", params)),
  retryEvent: (deliveryId: string) =>
    api<{ state: string }>(`/admin/events/${deliveryId}/retry`, {
      method: "POST",
    }),
  audit: (params: Params) =>
    api<Page<AdminAuditRow>>(withQuery("/admin/audit", params)),
  products: () => api<AdminProductRow[]>("/admin/products"),
  clients: (params: Params) =>
    api<AdminClientRow[]>(withQuery("/admin/clients", params)),
  updateProduct: (
    productId: string,
    body: { branding?: ProductBranding; web_url?: string | null },
  ) =>
    api<AdminProductRow>(`/admin/products/${productId}`, {
      method: "PATCH",
      body,
    }),
  rotateWebhookSecret: (productId: string) =>
    api<{ webhook_secret: string }>(
      `/admin/products/${productId}/webhook-secret/rotate`,
      { method: "POST" },
    ),
  rotateClientSecret: (clientId: string) =>
    api<{ client_secret: string }>(`/admin/clients/${clientId}/secret/rotate`, {
      method: "POST",
    }),
  disableClient: (clientId: string) =>
    api<{ status: string }>(`/admin/clients/${clientId}/disable`, {
      method: "POST",
    }),
  devices: (params: Params) =>
    api<Page<AdminDeviceRow>>(withQuery("/admin/devices", params)),
  deviceAction: (id: string, action: "revoke" | "disable" | "enable") =>
    api<AdminDeviceRow>(`/admin/devices/${id}/${action}`, { method: "POST" }),
  securityEvents: (params: Params) =>
    api<Page<AdminSecurityEventRow>>(
      withQuery("/admin/security-events", params),
    ),
  unlock: (body: { ip?: string; email?: string }) =>
    api<{ ip: boolean; email: boolean }>("/admin/security-events/unlock", {
      method: "POST",
      body,
    }),
  grantStaff: (body: { email: string; role: string }) =>
    api<StaffRow>("/admin/staff", { method: "POST", body }),
  metrics: (params: Params) =>
    api<AdminMetrics>(withQuery("/admin/metrics/overview", params)),
};
